import React, { useState, useCallback, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import DashboardSidebar from '../DashboardSidebar/DashboardSidebar';
import useSidebarHierarchy from '../../hooks/useSidebarHierarchy';
import { SidebarSelectionContext } from '../../contexts/SidebarSelectionContext';
import { SidebarSlotContext } from '../../contexts/SidebarSlotContext';
import './AppShell.css';

const EMPTY_SET = new Set();

function toggleInSet(prev, key) {
  const next = new Set(prev);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

export default function AppShell() {
  const sidebarProps = useSidebarHierarchy();

  // Sidebar slot — pages can portal into this to override the default sidebar.
  // ref-callback into state so the value triggers a re-render of consumers
  // (createPortal needs a real DOM node).
  const [slotEl, setSlotEl] = useState(null);
  const [hasOverride, setHasOverride] = useState(false);
  const slotValue = useMemo(() => ({ slotEl, setHasOverride }), [slotEl]);

  const [enabled, setEnabled] = useState(false);
  const [selectedDomains, setSelectedDomains] = useState(EMPTY_SET);
  const [selectedExams, setSelectedExams] = useState(EMPTY_SET);
  const [selectedTopics, setSelectedTopics] = useState(EMPTY_SET);
  // Pages can register their own click handlers here to override the default
  // toggle-into-Set behavior (e.g. /bank/drugs uses single-select filtering).
  const [topicClickOverride, setTopicClickOverride] = useState(null);
  const [examClickOverride, setExamClickOverride] = useState(null);
  const [domainClickOverride, setDomainClickOverride] = useState(null);

  const toggleDomain = useCallback((domain) => {
    setSelectedDomains(prev => toggleInSet(prev, domain));
  }, []);
  const toggleExam = useCallback((domain, examId) => {
    setSelectedExams(prev => toggleInSet(prev, `${domain}::${examId}`));
  }, []);
  const toggleTopic = useCallback((topicOrDomain, examId, topic) => {
    const key = (examId && topic) ? `${topicOrDomain}::${examId}::${topic}` : topicOrDomain;
    setSelectedTopics(prev => toggleInSet(prev, key));
  }, []);
  const clearSelection = useCallback(() => {
    setSelectedDomains(EMPTY_SET);
    setSelectedExams(EMPTY_SET);
    setSelectedTopics(EMPTY_SET);
  }, []);

  const selectionValue = useMemo(() => ({
    enabled,
    setEnabled,
    selectedDomains,
    selectedExams,
    selectedTopics,
    setSelectedDomains,
    setSelectedExams,
    setSelectedTopics,
    toggleDomain,
    toggleExam,
    toggleTopic,
    clearSelection,
    topicClickOverride,
    examClickOverride,
    domainClickOverride,
    setTopicClickOverride,
    setExamClickOverride,
    setDomainClickOverride,
  }), [enabled, selectedDomains, selectedExams, selectedTopics, toggleDomain, toggleExam, toggleTopic, clearSelection, topicClickOverride, examClickOverride, domainClickOverride]);

  return (
    <SidebarSelectionContext.Provider value={selectionValue}>
      <SidebarSlotContext.Provider value={slotValue}>
        <div className="app-shell">
          <div ref={setSlotEl} className="app-shell-sidebar-slot">
            {!hasOverride && <DashboardSidebar {...sidebarProps} />}
          </div>
          <main className="app-shell-main">
            <Outlet />
          </main>
        </div>
      </SidebarSlotContext.Provider>
    </SidebarSelectionContext.Provider>
  );
}
